import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../../api/client'
import { Loading } from '../../components/ui'

interface AdminStats {
  totalUsers: number
  totalJobs: number
  totalProducts: number
  totalTransactions: number
  recentUsers: number
  recentJobs: number
  jobsByStatus: Record<string, number>
  productsByStatus: Record<string, number>
}

interface PlatformWallet {
  currentBalance: number
  totalEarned: number
  productFees: { total: number; count: number }
  jobFees: { total: number; count: number }
  recentTransactions: Array<{
    id: string
    amount: number
    description: string
    createdAt: string
  }>
}

export default function AdminDashboardPage() {
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [wallet, setWallet] = useState<PlatformWallet | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [statsData, walletData] = await Promise.all([
        api.get<AdminStats>('/admin/stats'),
        api.get<PlatformWallet>('/admin/platform-wallet'),
      ])
      setStats(statsData)
      setWallet(walletData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stats')
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return <Loading message="Loading admin dashboard..." />
  }

  if (error) {
    return <div className="alert alert-error">{error}</div>
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Admin Dashboard</h1>
          <p className="text-secondary">Manage users, jobs, products, and transactions</p>
        </div>
      </div>

      {stats && (
        <>
          {/* Stats Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
            <Link to="/admin/users" className="card-static" style={{ textDecoration: 'none' }}>
              <h3 style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>Total Users</h3>
              <p style={{ fontSize: '32px', fontWeight: 'bold', color: 'var(--accent)' }}>{stats.totalUsers}</p>
              <p className="text-secondary" style={{ fontSize: '13px' }}>+{stats.recentUsers} this week</p>
            </Link>

            <Link to="/admin/jobs" className="card-static" style={{ textDecoration: 'none' }}>
              <h3 style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>Total Jobs</h3>
              <p style={{ fontSize: '32px', fontWeight: 'bold', color: 'var(--accent)' }}>{stats.totalJobs}</p>
              <p className="text-secondary" style={{ fontSize: '13px' }}>+{stats.recentJobs} this week</p>
            </Link>

            <Link to="/admin/products" className="card-static" style={{ textDecoration: 'none' }}>
              <h3 style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>Total Products</h3>
              <p style={{ fontSize: '32px', fontWeight: 'bold', color: 'var(--accent)' }}>{stats.totalProducts}</p>
            </Link>

            <Link to="/admin/transactions" className="card-static" style={{ textDecoration: 'none' }}>
              <h3 style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>Transactions</h3>
              <p style={{ fontSize: '32px', fontWeight: 'bold', color: 'var(--accent)' }}>{stats.totalTransactions}</p>
            </Link>
          </div>

          {/* Platform Wallet */}
          {wallet && (
            <div className="card-static" style={{ marginBottom: '2rem', background: 'linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-primary) 100%)', border: '2px solid var(--accent)' }}>
              <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
                Platform Wallet
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1.5rem' }}>
                <div>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Current Balance</p>
                  <p style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--success)' }}>{wallet.currentBalance} pts</p>
                </div>
                <div>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Total Earned</p>
                  <p style={{ fontSize: '28px', fontWeight: 'bold' }}>{wallet.totalEarned} pts</p>
                </div>
                <div>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Product Fees</p>
                  <p style={{ fontSize: '20px', fontWeight: 'bold' }}>{wallet.productFees.total} pts</p>
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{wallet.productFees.count} sales</p>
                </div>
                <div>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Job Fees</p>
                  <p style={{ fontSize: '20px', fontWeight: 'bold' }}>{wallet.jobFees.total} pts</p>
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{wallet.jobFees.count} completions</p>
                </div>
              </div>
              {wallet.recentTransactions.length > 0 && (
                <div style={{ marginTop: '1.5rem', borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
                  <h4 style={{ fontSize: '14px', marginBottom: '0.75rem' }}>Recent Fee Transactions</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '150px', overflowY: 'auto' }}>
                    {wallet.recentTransactions.slice(0, 5).map((tx) => (
                      <div key={tx.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                        <span className="text-secondary" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {tx.description}
                        </span>
                        <span style={{ fontWeight: 500, color: 'var(--success)', marginLeft: '1rem' }}>+{tx.amount} pts</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Status Breakdown */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <div className="card-static">
              <h3 style={{ marginBottom: '1rem' }}>Jobs by Status</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {Object.entries(stats.jobsByStatus).map(([status, count]) => (
                  <div key={status} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="text-secondary">{status.replace('_', ' ')}</span>
                    <span style={{ fontWeight: 'bold' }}>{count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="card-static">
              <h3 style={{ marginBottom: '1rem' }}>Products by Status</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {Object.entries(stats.productsByStatus).map(([status, count]) => (
                  <div key={status} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="text-secondary">{status.replace('_', ' ')}</span>
                    <span style={{ fontWeight: 'bold' }}>{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="card-static" style={{ marginTop: '1.5rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>Quick Actions</h3>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <Link to="/admin/users" className="btn">Manage Users</Link>
              <Link to="/admin/jobs" className="btn">Manage Jobs</Link>
              <Link to="/admin/products" className="btn">Manage Products</Link>
              <Link to="/admin/transactions" className="btn">View Transactions</Link>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
